// UI-24-1 — sustainability data helpers for the order view.
//
// On a booked order, `service.roomAmenities` is populated only for ZERO rooms
// (guest-added extras, per-stay price). For SUSTAINABLE rooms the list is empty —
// the omitted amenities + their deduction prices live in the room-type (supplier)
// config. That config is fetchable without a new backend endpoint because the
// "zero" tenant is the hotel owner: `service.supplierCode` starts with `company.`
// (see RuntimeStore.ownProduct / AccommodationService.isOwnProduct), so the
// org-scoped room-type endpoints are callable for the tenant's own orders.
//
// Path: hotelId (on the order) -> ownAccommodationContracts -> productId (contract id)
//       -> hotelRoomAmenities({productId, roomId: roomTypeId}) -> amenity config.
//   - omitted (Sustainable) = basicEnabled && !enabled (deduction = price)
//   - included (modal)      = enabled

interface AmenityView {
    id?: number | string
    amenityTypeId?: number | string
    name: string
    category?: string
    price?: number // per-stay amount (backend BigDecimal on the config amenity)
}

interface RoomTypeConfig {
    omitted: AmenityView[]
    included: AmenityView[]
}

interface SustainableRoom {
    service: any
    room: any
    level: 'SUSTAINABLE' | 'ZERO' | 'BASIC'
    hotelId: any
    roomTypeId: any
}

export interface BreakdownRow {
    name: string
    amount: number
    sign: '+' | '-'
    kind?: 'extra'
}

export interface Breakdown {
    // i18n key suffix under order_sustainability.*
    label: 'zero_room_rate' | 'standard_room_rate' | 'room_rate'
    subHeader: 'zero_amenities_included' | 'sustainable_omitted_services' | null
    baseRate: number
    rows: BreakdownRow[]
    total: number
    currency: string
}

// Page-lifetime caches so the room-block note and the price breakdown don't refetch.
const configCache = new Map<string, RoomTypeConfig | null>()
const contractIdCache = new Map<string, any>()

// Resolve the own-product contract id (productId) for a hotel. The "zero" tenant is a tour
// operator with contracts across many suppliers, so ownAccommodationContracts returns ALL of
// its contracts (the hotelContentId query param is ignored server-side) — we must pick the one
// whose hotelContentId matches the order's hotelId, NOT contracts[0] (which is a different hotel
// and yields the whole amenity catalog — tens of thousands of rows).
async function getProductId($api: any, hotelId: any): Promise<any> {
    const hk = String(hotelId)
    if (contractIdCache.has(hk)) return contractIdCache.get(hk)
    let productId: any = null
    try {
        const res = await $api.ownAccommodationContracts.get({active: true})
        const contracts = res?.ownProductContracts || []
        productId = contracts.find((c: any) => String(c.hotelContentId) === hk)?.id ?? null
    } catch {
        productId = null
    }
    contractIdCache.set(hk, productId)
    return productId
}

// Resolve a localized name (plain string or locale map) to a display string.
function localized(obj: any, locale: string): string {
    if (!obj) return ''
    if (typeof obj === 'string') return obj.replace('[object Object]', '').trim()
    if (obj[locale]) return String(obj[locale])
    if (obj.en) return String(obj.en)
    const keys = obj ? Object.keys(obj) : []
    return keys.length ? String(obj[keys[0]]) : ''
}

// Own-product check — mirrors RuntimeStore.ownProduct (supplierCode "company.*").
export function isOwnProductService(service: any): boolean {
    return /^company\./.test(service?.supplierCode || '')
}

// Cancelled-service check — an order is cancelled when its services are (status starts
// with "cancel", e.g. "Canceled"/"Cancelled"). The price breakdown shows all prices as 0
// for cancelled orders.
export function isServiceCancelled(service: any): boolean {
    return /^cancel/i.test(service?.status || '')
}

// Collect own-product accommodation rooms for the price breakdown — BASIC, SUSTAINABLE,
// and ZERO. BASIC rooms have no amenities (only extra services, if any), so they yield a
// "Room Rate" block with no explainer; the breakdown self-hides when a room has no rows.
export function getSustainableRooms(services: any[] = []): SustainableRoom[] {
    const out: SustainableRoom[] = []
    for (const service of services || []) {
        if (!isOwnProductService(service)) continue
        const rooms = service?.serviceDetails?.[0]?.rooms || []
        for (const room of rooms) {
            const raw = room?.sustainabilityLevel
            const level: SustainableRoom['level'] = raw === 'SUSTAINABLE' || raw === 'ZERO' ? raw : 'BASIC'
            out.push({
                service,
                room,
                level,
                hotelId: service?.serviceDetails?.[0]?.hotelId,
                roomTypeId: room?.roomTypeId,
            })
        }
    }
    return out
}

// Fetch the room-type amenity config (omitted/included) for a Sustainable room.
// Returns null when not an own product / fetch fails / contract not found.
export async function loadRoomTypeConfig(hotelId: any, roomTypeId: any): Promise<RoomTypeConfig | null> {
    if (!hotelId || !roomTypeId) return null
    const key = `${hotelId}:${roomTypeId}`
    if (configCache.has(key)) return configCache.get(key) ?? null
    try {
        const app = useNuxtApp()
        const locale = app.$i18n?.locale?.value || 'en'
        const $api = app.$api as any
        const productId = await getProductId($api, hotelId)
        if (!productId) {
            configCache.set(key, null)
            return null
        }
        const amenities = (await $api.hotelRoomAmenities.get({productId, roomId: roomTypeId, locale: 'all'})) || []
        // Guard against a mismatched productId/roomId returning the whole catalog (thousands).
        if (!Array.isArray(amenities) || amenities.length > 200) {
            configCache.set(key, null)
            return null
        }
        const toView = (a: any): AmenityView => ({
            id: a.id,
            amenityTypeId: a.amenityTypeId,
            name: localized(a.name, locale),
            category: a.category,
            price: a.price != null ? Number(a.price) : undefined,
        })
        const result: RoomTypeConfig = {
            omitted: amenities.filter((a: any) => a.basicEnabled && !a.enabled).map(toView),
            included: amenities.filter((a: any) => a.enabled).map(toView),
        }
        configCache.set(key, result)
        return result
    } catch {
        configCache.set(key, null)
        return null
    }
}

// Build the rate breakdown. baseRate is DERIVED so the rows always reconcile to
// the order total: baseRate + (added) - (omitted) = total.
export function deriveBreakdown(
    rooms: SustainableRoom[],
    configs: Record<string, RoomTypeConfig | null>,
    orderTotal: number,
    orderCurrency: string
): Breakdown | null {
    const rows: BreakdownRow[] = []
    let hasZero = false
    let hasSustainable = false
    // roomAmenities live on the service (not per room) — count each service once, even if it has
    // several ZERO rooms, so its amenities aren't summed multiple times.
    const processedZeroServices = new Set<any>()
    // Booked extra services (e.g. Tablet) live on the service and apply to ALL room types
    // (BASIC/ZERO/SUSTAINABLE) — count each service once.
    const processedExtraServices = new Set<any>()
    const addExtraServices = (service: any) => {
        if (!service || processedExtraServices.has(service)) return
        processedExtraServices.add(service)
        for (const es of service.extraServices || []) {
            const cp = es?.prices?.find((p: any) => p.priceType === 'CLIENT')
            const amt = Number(cp?.amount ?? 0)
            if (amt) rows.push({name: es.name || String(es?.id ?? ''), amount: amt, sign: '+', kind: 'extra'})
        }
    }

    for (const r of rooms) {
        // Extra services apply to ALL room types (BASIC/ZERO/SUSTAINABLE).
        addExtraServices(r.service)
        if (r.level === 'ZERO') {
            hasZero = true
            if (processedZeroServices.has(r.service)) continue
            processedZeroServices.add(r.service)
            for (const a of r.service?.roomAmenities || []) {
                // Booked amenity price on the order is {currency, value} (per-stay, unit x count).
                const amt = Number(a?.price?.value ?? a?.price?.totalPrice ?? 0)
                const name = typeof a?.name === 'string' ? a.name : localized(a?.name, 'en')
                if (amt) rows.push({name: name || String(a?.amenityTypeId ?? ''), amount: amt, sign: '+'})
            }
        } else if (r.level === 'SUSTAINABLE') {
            hasSustainable = true
            const cfg = configs[`${r.hotelId}:${r.roomTypeId}`]
            for (const a of cfg?.omitted || []) {
                if (a.price) rows.push({name: a.name, amount: a.price, sign: '-'})
            }
        }
    }

    if (!rows.length) return null

    const sumAdded = rows.filter(r => r.sign === '+').reduce((s, r) => s + r.amount, 0)
    const sumOmitted = rows.filter(r => r.sign === '-').reduce((s, r) => s + r.amount, 0)
    // Cancelled order: show all prices as 0 (extras, amenities, base rate, total).
    const cancelled = rooms.length > 0 && rooms.every(r => isServiceCancelled(r.service))
    const baseRate = cancelled ? 0 : orderTotal - sumAdded + sumOmitted
    const label: Breakdown['label'] =
        hasZero && !hasSustainable ? 'zero_room_rate' : hasSustainable && !hasZero ? 'standard_room_rate' : 'room_rate'
    const subHeader: Breakdown['subHeader'] =
        hasZero && !hasSustainable
            ? 'zero_amenities_included'
            : hasSustainable && !hasZero
              ? 'sustainable_omitted_services'
              : null
    const outRows = cancelled ? rows.map(r => ({...r, amount: 0})) : rows

    return {label, subHeader, baseRate, rows: outRows, total: cancelled ? 0 : orderTotal, currency: orderCurrency}
}

// Per-service CLIENT price (amount + currency) — used as each room's total in the
// per-room breakdown. Order salesTerms: .find(type==='CLIENT').price = {amount, currency}.
export function serviceClientAmount(service: any): {amount: number; currency: string} | null {
    const cp = service?.salesTerms?.find((p: any) => p.type === 'CLIENT')?.price
    if (!cp) return null
    return {amount: Number(cp.amount ?? 0), currency: cp.currency || ''}
}

// Per-room breakdown (UI-24-1 follow-up: one fieldset per room, not one merged block).
// baseRate is DERIVED so the room's rows reconcile to that room's CLIENT total:
// baseRate + (added) - (omitted) = roomTotal. Returns null when the room has no rows
// (e.g. a SUSTAINABLE room whose room-type config has no priced omitted amenities).
export function deriveRoomBreakdown(
    room: SustainableRoom,
    config: RoomTypeConfig | null,
    roomTotal: number,
    currency: string
): Breakdown | null {
    const rows: BreakdownRow[] = []
    // Booked extra services (e.g. Tablet) apply to ALL room types (BASIC/ZERO/SUSTAINABLE).
    for (const es of room.service?.extraServices || []) {
        const cp = es?.prices?.find((p: any) => p.priceType === 'CLIENT')
        const amt = Number(cp?.amount ?? 0)
        if (amt) rows.push({name: es.name || String(es?.id ?? ''), amount: amt, sign: '+', kind: 'extra'})
    }
    if (room.level === 'ZERO') {
        // roomAmenities live on the service (per-stay, {currency, value}).
        for (const a of room.service?.roomAmenities || []) {
            const amt = Number(a?.price?.value ?? a?.price?.totalPrice ?? 0)
            const name = typeof a?.name === 'string' ? a.name : localized(a?.name, 'en')
            if (amt) rows.push({name: name || String(a?.amenityTypeId ?? ''), amount: amt, sign: '+'})
        }
    } else if (room.level === 'SUSTAINABLE') {
        // SUSTAINABLE: omitted amenities (deductions) come from the room-type config.
        for (const a of config?.omitted || []) {
            if (a.price) rows.push({name: a.name, amount: a.price, sign: '-'})
        }
    }
    // BASIC (or unmapped level): only extra services, which were added above.

    if (!rows.length) return null

    const sumAdded = rows.filter(r => r.sign === '+').reduce((s, r) => s + r.amount, 0)
    const sumOmitted = rows.filter(r => r.sign === '-').reduce((s, r) => s + r.amount, 0)
    // Cancelled service: show all prices as 0 (extras, amenities, base rate, total).
    const cancelled = isServiceCancelled(room.service)
    const baseRate = cancelled ? 0 : roomTotal - sumAdded + sumOmitted
    const label: Breakdown['label'] =
        room.level === 'ZERO' ? 'zero_room_rate' : room.level === 'SUSTAINABLE' ? 'standard_room_rate' : 'room_rate'
    const subHeader: Breakdown['subHeader'] =
        room.level === 'ZERO'
            ? 'zero_amenities_included'
            : room.level === 'SUSTAINABLE'
              ? 'sustainable_omitted_services'
              : null
    const outRows = cancelled ? rows.map(r => ({...r, amount: 0})) : rows

    return {label, subHeader, baseRate, rows: outRows, total: cancelled ? 0 : roomTotal, currency}
}
